import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";

import { Box, MenuItem } from "@mui/material";
import JoditEditor, { IJoditEditorProps } from "jodit-react";
import { useTranslation } from "react-i18next";
import isEmail from "validator/lib/isEmail";
import * as yup from "yup";

import { useAppState } from "../../../../../context/StateContext";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { EmailTemplateToSend } from "../../../../../models/templates/email.template";
import { Placement, UsedInEmailTemplates } from "../../../../../util/enums";
import { templateTags } from "../../../../../constants";
import { handleInsertTemplateTag } from "../../../../../util/joditEditor";
import { Input, Select } from "../../../../Form";
import { FormError } from "../../../../Form/FormError";
import Checkbox from "../../../../Form/Checkbox";
import { AppSwitch } from "../../../AppSwitch";
import { NotificationToast } from "../../../Toast/NotificationToast";
import { useRequests } from "../../../SendEmailModal/hooks/useRequests";
import SkeletonComponent from "../../../Skeleton";
import ScheduleSendModal from "../ScheduleSendModal";
import CommunicationCentreService from "../../../../../services/communication.centre.service";
import { NotificationTemplate } from "../../../../../models/notification.template";
import { Status } from "../../../../../util/enums";
import { useQuery, useMutation } from "@tanstack/react-query";
import { EntityContext } from "../../index";
import { getTypeFromEntityContext } from "../../utils";
import {
  EmailEditorContainer,
  SectionTitle,
  ToggleRow,
  ToggleLabel,
  FormRow,
  EditorContainer,
  EditorOptionRow,
  EditorOption,
} from "./styles";

const SHIPEEZI_LOGO_URL =
  "https://shipeezi-images.s3.ap-southeast-2.amazonaws.com/shipeezi-logo.png";

const extractBodyFromHtml = (html: string): string => {
  if (!html) return "";
  const mainMatch = html.match(/<main[^>]*>([\s\S]*?)<\/main>/i);
  if (mainMatch?.[1]) return mainMatch[1].trim();
  const hasHeader = html.includes("<header");
  const hasFooter = html.includes("<footer");
  const hasTableWrapper = html.includes("<table") && html.includes("cellpadding");
  if (hasHeader || hasFooter || hasTableWrapper) {
    let body = html.replace(/<header[^>]*>[\s\S]*?<\/header>/gi, "");
    body = body.replace(/<footer[^>]*>[\s\S]*?<\/footer>/gi, "");
    body = body.replace(/<table[^>]*>[\s\S]*?<td[^>]*>/gi, "");
    body = body.replace(/<\/td>[\s\S]*?<\/tr>[\s\S]*?<\/table>/gi, "");
    return body.trim();
  }
  return html.trim();
};

const detectEmailOptionsFromHtml = (html: string) => {
  if (!html) return { showHeader: true, showCopyrightText: true };
  const hasHeader = /<header[^>]*>/i.test(html);
  const footerMatch = html.match(/<footer[^>]*>([\s\S]*?)<\/footer>/i);
  const showCopyrightText = footerMatch?.[1]
    ? /©|copyright/i.test(footerMatch[1])
    : false;
  return { showHeader: hasHeader, showCopyrightText };
};

interface EmailEditorProps {
  entityContext: EntityContext;
  usedIn?: UsedInEmailTemplates;
  onSend: () => void;
  onCancel: () => void;
}

export interface EmailEditorHandle {
  sendEmail: (scheduledDate?: string) => void;
  isLoading: boolean;
}

const EmailEditor = forwardRef<EmailEditorHandle, EmailEditorProps>(
  ({ entityContext, usedIn = UsedInEmailTemplates.ORDERS, onSend, onCancel }, ref) => {
    const { t: translate } = useTranslation();
    const { currentUser } = useAppState();
    const { getAllEmailTemplates, sendEmailTo, useProcessEmailTemplateRequest } = useRequests();
    const textEditorRef = useRef(null);

    const entityId = entityContext.entityId;
    const entity = entityContext.entity;
    const customer = entityContext.customer;

    const getEntityReference = () => {
      if ("reference" in entity) {
        return entity.reference;
      }
      return undefined;
    };

    const getUserFullName = () => {
      if ("user" in entity && entity.user) {
        return entity.user.fullName;
      }
      return undefined;
    };

    const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<NotificationTemplate>(
      {} as NotificationTemplate
    );
    const [sendEmailTemplate, setSendEmailTemplate] = useState<EmailTemplateToSend>(
      {} as EmailTemplateToSend
    );
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [errors, setErrors] = useState<yup.ValidationError[]>([]);
    const [plainTextMode, setPlainTextMode] = useState(false);
    const [showHeader, setShowHeader] = useState(true);
    const [showCopyrightText, setShowCopyrightText] = useState(true);
    const [shipeeziBranding, setShipeeziBranding] = useState(true);
    const [customerLogo, setCustomerLogo] = useState(false);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

    const { fieldHasError, getErrorByField } = useYupValidation(errors);

    const { data: emailTemplatesResponse, isLoading: isEmailTemplatesLoading } = useQuery(
      ["communication-centre.templates.email", usedIn, currentUser?.companyId],
      () =>
        CommunicationCentreService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          searchByJsonSearch: JSON.stringify({
            available_in: `'${usedIn}'`,
          }),
          sortBy: JSON.stringify([["name", "asc"]]),
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        select: (data) => ({
          ...data,
          data:
            data?.data?.filter((template: NotificationTemplate) =>
              template.notificationTypes?.includes("email")
            ) || [],
        }),
      }
    );

    const emailTemplates = emailTemplatesResponse?.data || [];

    const { mutate: sendTemplate, isLoading: isLoading } = useMutation(
      ["send.notification.template"],
      async (payload: any) => {
        return await CommunicationCentreService.sendTemplate(payload);
      },
      {
        onSuccess: () => {
          NotificationToast.success(
            translate("communicationSentSuccessfully") || "Communication sent successfully"
          );
          onSend();
        },
        onError: (error: any) => {
          NotificationToast.error(
            error?.response?.data?.message ||
              translate("anErrorOccurredTryAgain") ||
              "An error occurred"
          );
        },
      }
    );

    const adaptEmailLogo = useCallback(
      (html: string): string => {
        const customerLogoSrc = currentUser?.company?.logo?.mediaLink;
        const SHIPEEZI_IMG = `<img style="margin:0;border:0;padding:0;display:block;width:121px;height:29px;" src="${SHIPEEZI_LOGO_URL}" alt="Shipeezi logo" width="121" height="29" />`;
        const CUSTOMER_IMG = customerLogoSrc
          ? `<img style="margin:0;border:0;padding:0;display:block;width:121px;height:29px;" src="${customerLogoSrc}" alt="Customer logo" width="121" height="29" />`
          : SHIPEEZI_IMG;
        return html
          .replace(/#TAG_SHIPEEZI_LOGO/g, SHIPEEZI_IMG)
          .replace(/#TAG_CUSTOMER_LOGO/g, CUSTOMER_IMG);
      },
      [currentUser]
    );

    const composeDisplayHtml = useCallback(
      (
        bodyContent: string,
        headerEnabled: boolean,
        copyrightEnabled: boolean,
        useCustomerLogo: boolean,
        fromName: string
      ): string => {
        const logoTag = useCustomerLogo ? "#TAG_CUSTOMER_LOGO" : "#TAG_SHIPEEZI_LOGO";
        const header = headerEnabled
          ? `<header style="padding:24px 32px;background:#F5F7FA;border-bottom:1px solid #E4E7EC;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>${adaptEmailLogo(logoTag)}</td>
                  <td style="font-family:Manrope,Arial,sans-serif;font-size:14px;color:#475467;text-align:right;">${fromName}</td>
                </tr>
              </table>
            </header>`
          : "";
        const footerParts: string[] = [];
        if (copyrightEnabled) {
          footerParts.push(`© ${new Date().getFullYear()} ${fromName || "Shipeezi"}`);
        }
        footerParts.push("Powered by Shipeezi");
        const footer = `<footer style="padding:24px 32px;background:#F5F7FA;border-top:1px solid #E4E7EC;text-align:center;font-family:Manrope,Arial,sans-serif;font-size:12px;color:#475467;">${footerParts.join("<br/>")}</footer>`;
        return `<table width="100%" cellpadding="0" cellspacing="0" style="font-family:Manrope,Arial,sans-serif;background:#FFFFFF;"><tr><td>${header}<main style="padding:32px;">${bodyContent}</main>${footer}</td></tr></table>`;
      },
      [adaptEmailLogo]
    );

    const { mutate: processEmailTemplate } = useProcessEmailTemplateRequest({
      onSuccess: ({ html: processedBody }) => {
        const fromName =
          sendEmailTemplate.from || currentUser?.company?.name || "";
        const fullHtml = composeDisplayHtml(
          processedBody,
          showHeader,
          showCopyrightText,
          customerLogo,
          fromName
        );
        setSendEmailTemplate((prev) => ({ ...prev, html: fullHtml }));
      },
    });

    const hasLogoTag = useMemo(
      () =>
        (selectedEmailTemplate.html || "").includes("#TAG_SHIPEEZI_LOGO") ||
        (selectedEmailTemplate.html || "").includes("#TAG_CUSTOMER_LOGO"),
      [selectedEmailTemplate.html]
    );

    const recomposeHtml = useCallback(
      (
        headerEnabled: boolean,
        copyrightEnabled: boolean,
        useCustomerLogo: boolean
      ) => {
        const currentBody = extractBodyFromHtml(
          sendEmailTemplate.html || ""
        );
        const fromName =
          sendEmailTemplate.from || currentUser?.company?.name || "";
        const newHtml = composeDisplayHtml(
          currentBody,
          headerEnabled,
          copyrightEnabled,
          useCustomerLogo,
          fromName
        );
        setSendEmailTemplate((prev) => ({ ...prev, html: newHtml }));
      },
      [sendEmailTemplate.html, sendEmailTemplate.from, currentUser, composeDisplayHtml]
    );

    const handleShowHeaderToggle = useCallback(
      (checked: boolean) => {
        setShowHeader(checked);
        recomposeHtml(checked, showCopyrightText, customerLogo);
      },
      [recomposeHtml, showCopyrightText, customerLogo]
    );

    const handleShowCopyrightTextToggle = useCallback(
      (checked: boolean) => {
        setShowCopyrightText(checked);
        recomposeHtml(showHeader, checked, customerLogo);
      },
      [recomposeHtml, showHeader, customerLogo]
    );

    const handleCustomerLogoToggle = useCallback(
      (checked: boolean) => {
        setCustomerLogo(checked);
        recomposeHtml(showHeader, showCopyrightText, checked);
      },
      [recomposeHtml, showHeader, showCopyrightText]
    );

    const schema = yup.object().shape({
      selectedEmailTemplateId: yup.number().typeError(translate("thisFieldIsRequired")),
      from: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      subject: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      html: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      to: yup
        .string()
        .trim()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test("email-check", translate("thisFieldIsRequired"), (email) => {
          if (!email) return true;
          return isEmail(email);
        }),
    });

    useEffect(() => {
      if (currentUser?.company?.name) {
        setSendEmailTemplate((prev) => ({
          ...prev,
          from: currentUser.company.name,
        }));
      }
      if (customer?.email) {
        setSendEmailTemplate((prev) => ({
          ...prev,
          to: customer.email || "",
        }));
      }
    }, [currentUser, customer]);

    useEffect(() => {
      if (!selectedEmailTemplate.html) return;
      const rawHtml = selectedEmailTemplate.html;
      const detectedOptions = detectEmailOptionsFromHtml(rawHtml);
      const detectedCustomerLogo = rawHtml.includes("#TAG_CUSTOMER_LOGO");
      setShowHeader(detectedOptions.showHeader);
      setShowCopyrightText(detectedOptions.showCopyrightText);
      setCustomerLogo(detectedCustomerLogo);
      const bodyContent = extractBodyFromHtml(rawHtml);
      processEmailTemplate({
        data: {
          companyName: currentUser?.company?.name,
          customerFullName: customer?.fullName,
          quoteReference: getEntityReference(),
          userFullname: getUserFullName(),
        },
        html: bodyContent || rawHtml,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEmailTemplate.id]);

    const emailLayoutOptions = useMemo(
      () =>
        [
          {
            field: "plainTextMode",
            label: translate("plainText") || "Plain text",
            checked: plainTextMode,
            disabled: false,
            onChange: setPlainTextMode,
          },
          {
            field: "showHeader",
            label: translate("header") || "Header",
            checked: showHeader,
            disabled: false,
            onChange: handleShowHeaderToggle,
          },
          {
            field: "showCopyrightText",
            label: translate("copyrightText") || "Copyright text",
            checked: showCopyrightText,
            disabled: false,
            onChange: handleShowCopyrightTextToggle,
          },
          {
            field: "customerLogo",
            label: translate("customerLogo") || "Customer logo",
            checked: customerLogo,
            disabled: !hasLogoTag && !showHeader,
            onChange: handleCustomerLogoToggle,
          },
          {
            field: "shipeeziBranding",
            label: translate("shipeeziBranding") || "Shipeezi branding",
            checked: shipeeziBranding,
            disabled: true,
            onChange: setShipeeziBranding,
          },
        ] as const,
      [
        translate,
        plainTextMode,
        showHeader,
        showCopyrightText,
        customerLogo,
        shipeeziBranding,
        hasLogoTag,
        handleShowHeaderToggle,
        handleShowCopyrightTextToggle,
        handleCustomerLogoToggle,
      ]
    );

    const config: IJoditEditorProps["config"] = {
      height: "326px",
      toolbarButtonSize: "middle",
      removeButtons: ["fullsize"],
      buttons: [
        "fontsize",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "left",
        "center",
        "right",
        "justify",
        "ul",
        "ol",
        "|",
        "image",
        "table",
        "|",
        "undo",
        "redo",
      ],
      controls: {
        customer: {
          name: (translate("customer") as string) || "Customer",
          exec: handleInsertTemplateTag,
          list: templateTags.customer,
        },
        quote: {
          name: (translate("quote") as string) || "Quote",
          exec: handleInsertTemplateTag,
          list: templateTags.quote,
        },
        order: {
          name: (translate("order") as string) || "Order",
          exec: handleInsertTemplateTag,
          list: templateTags.order,
        },
        shipment: {
          name: (translate("shipment") as string) || "Shipment",
          exec: handleInsertTemplateTag,
          list: templateTags.shipment,
        },
      },
      uploader: { insertImageAsBase64URI: true },
      extraButtons: ["quote", "order", "shipment", "customer"],
    };

    const onValidation = (email: EmailTemplateToSend) => {
      try {
        schema.validateSync(
          {
            selectedEmailTemplateId: selectedEmailTemplate?.id,
            from: email.from,
            subject: email.subject,
            html: email.html,
            to: email.to,
          },
          {
            abortEarly: false,
          }
        );
        setErrors([]);
        return true;
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          setErrors(error.inner);
        }
        return false;
      }
    };

    const sendEmailHandler = (scheduledDate?: string) => {
      const emailToValidate: EmailTemplateToSend = {
        ...sendEmailTemplate,
        subject: sendEmailTemplate.subject || selectedEmailTemplate.subject || "",
        from: sendEmailTemplate.from || currentUser?.company?.name || "",
        html: sendEmailTemplate.html || selectedEmailTemplate.html || "",
        to: sendEmailTemplate.to || "",
      };

      if (!onValidation(emailToValidate)) {
        return;
      }

      if (!entityId) {
        NotificationToast.error(translate("anErrorOccurredTryAgain") || "An error occurred");
        return;
      }

      // Start with the selected template (or empty template if none selected)
      const templateData: NotificationTemplate = selectedEmailTemplate.id
        ? { ...selectedEmailTemplate }
        : ({} as NotificationTemplate);

      // Overlay user edits - preserve all template fields, only update edited ones
      const updatedTemplate: NotificationTemplate = {
        ...templateData,
        // Apply user edits to template fields
        html: sendEmailTemplate.html || templateData.html || "",
        subject: sendEmailTemplate.subject || templateData.subject || "",
        fromEmail:
          sendEmailTemplate.from || templateData.fromEmail || currentUser?.company?.name || "",
        fromName:
          sendEmailTemplate.from || templateData.fromName || currentUser?.company?.name || "",
      };

      // Construct the payload for the new endpoint
      // Include the full NotificationTemplate with user edits, plus notificationType and notificationItem
      // Also include email-specific fields (to, cc, bcc) as they are user edits
      const payload = {
        ...updatedTemplate,
        // Include email-specific fields that user may have edited
        to: sendEmailTemplate.to || "",
        cc: sendEmailTemplate.cc || "",
        bcc: sendEmailTemplate.bcc || "",
        notificationType: "email" as const,
        notificationItem: {
          id: entityId,
          type: getTypeFromEntityContext(entityContext),
        },
      };

      sendTemplate(payload);
    };

    const handleScheduleSend = (scheduledDate: string) => {
      sendEmailHandler(scheduledDate);
      setScheduleModalOpen(false);
    };

    useImperativeHandle(ref, () => ({
      sendEmail: (scheduledDate?: string) => sendEmailHandler(scheduledDate),
      isLoading,
    }));

    return (
      <EmailEditorContainer>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", padding: "16px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <SectionTitle>{translate("emailDetails") || "Email Details"}</SectionTitle>
              <ToggleRow>
                <ToggleLabel>{translate("showCC") || "Show CC"}</ToggleLabel>
                <AppSwitch checked={showCc} onChange={(e) => setShowCc(e.target.checked)} />
                <ToggleLabel>{translate("showBCC") || "Show BCC"}</ToggleLabel>
                <AppSwitch checked={showBcc} onChange={(e) => setShowBcc(e.target.checked)} />
              </ToggleRow>
            </Box>

            <FormRow>
              <Box sx={{ flex: 1 }}>
                <SkeletonComponent isLoading={isEmailTemplatesLoading} isInput>
                  <Select
                    name="template"
                    label={translate("template") || "Template"}
                    value={selectedEmailTemplate?.id || ""}
                    onChange={(event) => {
                      if (Number(event.target.value)) {
                        const emailTemplate = emailTemplates?.find(
                          (template: NotificationTemplate) =>
                            template.id === Number(event.target.value)
                        );

                        if (emailTemplate) {
                          const rawHtml = emailTemplate.html || "";
                          const detectedOptions = detectEmailOptionsFromHtml(rawHtml);
                          const detectedCustomerLogo = rawHtml.includes("#TAG_CUSTOMER_LOGO");

                          setSelectedEmailTemplate(emailTemplate);
                          setSendEmailTemplate((prev) => ({
                            ...prev,
                            subject: emailTemplate.subject || prev.subject,
                          }));
                          setShowHeader(detectedOptions.showHeader);
                          setShowCopyrightText(detectedOptions.showCopyrightText);
                          setCustomerLogo(detectedCustomerLogo);

                          const bodyContent = extractBodyFromHtml(rawHtml);
                          if (bodyContent) {
                            processEmailTemplate({
                              data: {
                                companyName: currentUser?.company?.name,
                                customerFullName: customer?.fullName,
                                quoteReference: getEntityReference(),
                                userFullname: getUserFullName(),
                              },
                              html: bodyContent,
                            });
                          } else {
                            const fromName =
                              sendEmailTemplate.from || currentUser?.company?.name || "";
                            const fullHtml = composeDisplayHtml(
                              rawHtml,
                              detectedOptions.showHeader,
                              detectedOptions.showCopyrightText,
                              detectedCustomerLogo,
                              fromName
                            );
                            setSendEmailTemplate((prev) => ({
                              ...prev,
                              html: fullHtml,
                            }));
                          }
                        }
                      } else {
                        setSelectedEmailTemplate({} as NotificationTemplate);
                        setCustomerLogo(false);
                        setSendEmailTemplate((prev) => ({ ...prev, html: "" }));
                      }
                    }}
                    placeholder={translate("selectATemplate") || "Select a template"}
                    error={fieldHasError("selectedEmailTemplateId")}
                    errorMessage={getErrorByField("selectedEmailTemplateId")}
                    fullWidth
                  >
                    {emailTemplates?.map((template: NotificationTemplate) => (
                      <MenuItem key={template.name} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </SkeletonComponent>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Input
                  label={translate("from") || "From"}
                  value={sendEmailTemplate?.from || currentUser?.company?.name || ""}
                  disabled
                  fullWidth
                  required
                  error={fieldHasError("from")}
                  errorMessage={getErrorByField("from")}
                />
              </Box>
            </FormRow>

            <FormRow>
              <Box sx={{ flex: 1 }}>
                <Input
                  label={translate("subject") || "Subject"}
                  value={sendEmailTemplate?.subject || ""}
                  onChange={(event) =>
                    setSendEmailTemplate((prev) => ({ ...prev, subject: event.target.value }))
                  }
                  placeholder={translate("subject") || "Subject"}
                  fullWidth
                  required
                  error={fieldHasError("subject")}
                  errorMessage={getErrorByField("subject")}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Input
                  label={translate("to") || "To"}
                  value={sendEmailTemplate?.to || customer?.email || ""}
                  onChange={(event) =>
                    setSendEmailTemplate((prev) => ({ ...prev, to: event.target.value }))
                  }
                  placeholder={translate("to") || "To"}
                  fullWidth
                  required
                  error={fieldHasError("to")}
                  errorMessage={getErrorByField("to")}
                />
              </Box>
            </FormRow>

            {showCc && (
              <FormRow>
                <Box sx={{ flex: 1 }}>
                  <Input
                    label={translate("cc") || "CC"}
                    value={sendEmailTemplate?.cc || ""}
                    onChange={(event) =>
                      setSendEmailTemplate((prev) => ({ ...prev, cc: event.target.value }))
                    }
                    placeholder={translate("cc") || "CC"}
                    fullWidth
                  />
                </Box>
              </FormRow>
            )}

            {showBcc && (
              <FormRow>
                <Box sx={{ flex: 1 }}>
                  <Input
                    label={translate("bcc") || "BCC"}
                    value={sendEmailTemplate?.bcc || ""}
                    onChange={(event) =>
                      setSendEmailTemplate((prev) => ({ ...prev, bcc: event.target.value }))
                    }
                    placeholder={translate("bcc") || "BCC"}
                    fullWidth
                  />
                </Box>
              </FormRow>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <SectionTitle>
              {translate("emailContent") || "Email Content"}
              <span style={{ color: "#DC3545" }}>{" *"}</span>
            </SectionTitle>
            <FormError isError={!!getErrorByField("html")}>{getErrorByField("html")}</FormError>
            <EditorContainer>
              <JoditEditor
                ref={textEditorRef}
                value={sendEmailTemplate?.html || ""}
                config={config}
                onBlur={(newContent) => {
                  setSendEmailTemplate((prev) => ({ ...prev, html: newContent }));
                }}
              />
              <EditorOptionRow>
                {emailLayoutOptions.map((option) => (
                  <EditorOption key={option.field}>
                    <Checkbox
                      checked={option.checked}
                      onChange={(e) => option.onChange(e.target.checked)}
                      disabled={option.disabled}
                      label={option.label}
                      labelPlacement={Placement.end}
                    />
                  </EditorOption>
                ))}
              </EditorOptionRow>
            </EditorContainer>
          </Box>

        </Box>

        <ScheduleSendModal
          open={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          onSchedule={handleScheduleSend}
          communicationType="email"
          isLoading={isLoading}
        />
      </EmailEditorContainer>
    );
  }
);

EmailEditor.displayName = "EmailEditor";

export default EmailEditor;
